/**
 * Deciding whether a completed copy/move has actually shown up in the destination grid.
 *
 * Why this exists: `CreateCopyJobs` is the async migration API, and `getCopyJobProgress`
 * reporting `JobState 0` means the job has LEFT THE QUEUE — not that the destination list
 * will already return the new items. A single reload on completion therefore races the
 * server and often lands just before they become queryable.
 *
 * The first attempt at covering that was a blind timer: reload three more times, ten
 * seconds apart. It reloaded four times over thirty seconds after EVERY move — resetting
 * scroll and re-shimmering a grid the user was already reading — and, because the
 * completion flag stays raised, restarted that cycle every time they navigated back.
 *
 * So: ask instead of guess. If the items are already there, do nothing at all.
 */

const normalizedName = (value: any): string =>
    typeof value?.FileLeafRef === "string" ? value.FileLeafRef.trim().toLowerCase() : "";

/**
 * The file names a completed operation should have put in the destination.
 *
 * `FileLeafRef` rather than `UniqueId`: a move mints a NEW item at the destination, so the
 * source ids do not survive it. Names are compared case-insensitively, as SharePoint
 * treats them.
 */
export const getExpectedArrivalNames = (files: any[]): string[] =>
    Array.from(new Set((files ?? []).map(normalizedName).filter(Boolean)));

/**
 * Whether every expected name is present in the destination rows.
 *
 * Set-based so a large queue against a large grid stays linear rather than quadratic.
 *
 * Returns true for an empty expectation — nothing to wait for is not the same as
 * something missing, and the caller must not retry on it. A false negative is harmless
 * and bounded: the caller retries a fixed number of times and then stops regardless, so a
 * copy renamed by NameConflictBehavior, or an item sorted onto a later page, costs one
 * extra reload rather than an endless cycle.
 */
export const haveArrived = (rows: any[], expectedNames: string[]): boolean => {
    if (!expectedNames || expectedNames.length === 0) {
        return true;
    }

    const present = new Set<string>();
    // Rows can contain nulls: the grid uses them as virtual-scrolling placeholders.
    (rows ?? []).forEach(row => {
        const name = normalizedName(row);
        if (name) {
            present.add(name);
        }
    });

    return expectedNames.every(name => present.has(name));
};
